import { Box } from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { DeliveryDTO } from '../../api/generated';
import { useDeliveryImage } from '../../api/queries/deliveryImage';
import { useEdgeEstimation } from '../../api/queries/edgeEstimation';
import { useMaterialClassification } from '../../api/queries/materialClassification';
import { useSoilClassification } from '../../api/queries/soilClassification';
import { useVolumeEstimation } from '../../api/queries/volumeEstimation';
import { WrapperCardWithTitle } from '../../layouts/WrapperCardWithTitle';
import { DeliveryNote } from '../Delivery/DeliveryNote';
import { DetailImageQueryGuarded } from '../DetailView/DetailImageQuery';
import { FeedbackSnackbar } from '../FeedbackSnackbar';
import { AttributesColumnQueryGuarded } from '../Prediction/AttributesColumn/AttributesColumn';
import { MaterialColumnQueryGuarded } from '../Prediction/MaterialColumn/MaterialColumn';
import { PredictionColoringTooltip } from '../Prediction/PredictionColoringTooltip';
import { PredictionColumnsLayout } from '../Prediction/PredictionColumnsLayout';
import { shouldDisplayEdgeEstimation, shouldDisplaySoilClassification } from '../Prediction/utils';
import { useFeedbackSnackbar } from '../useFeedbackSnackbar';
import { BoundingBox } from './ImageCard';
import BillableMaterialColumn from '../BillableMaterials/predictionColumn/BillableMaterialColumn';
import { useResolvedBillableMaterials } from '../BillableMaterials/useResolvedBillableMaterials';
const pictureIdx = 0; // static pictureIdx, since we currently don't support having multiple pictures per delivery

export interface DeliveryDetailsProps {
  organisationId: string;
  isUserAdmin: boolean;
  delivery: DeliveryDTO;
  capturer_id?: string;
}

export const DeliveryDetails = ({
  organisationId,
  isUserAdmin,
  delivery,
  capturer_id,
}: DeliveryDetailsProps): ReactElement => {
  const image = useDeliveryImage({
    jobsiteId: delivery.jobsiteId,
    deliveryId: delivery.id,
    imageId: delivery.pictureIds[pictureIdx],
  });

  const materialClassification = useMaterialClassification({
    jobsiteId: delivery.jobsiteId,
    imageId: delivery.pictureIds[pictureIdx],
  });

  const displaySoilClassification = useMemo(
    () => shouldDisplaySoilClassification(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const soilClassification = useSoilClassification({
    jobsiteId: delivery.jobsiteId,
    imageId: delivery.pictureIds[pictureIdx],
    enabled: displaySoilClassification,
  });
  const volumeEstimation = useVolumeEstimation({
    jobsiteId: delivery.jobsiteId,
    imageId: delivery.pictureIds[pictureIdx],
  });

  // Conditionally enable edge estimation based on material classification
  const displayEdgeEstimation = useMemo(
    () => shouldDisplayEdgeEstimation(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const edgeEstimation = useEdgeEstimation({
    jobsiteId: delivery.jobsiteId,
    imageId: delivery.pictureIds[pictureIdx],
    enabled: displayEdgeEstimation && !!delivery.jobsiteId && !!delivery.pictureIds[pictureIdx],
  });

  const { resolvedMaterials } = useResolvedBillableMaterials(
    organisationId,
    delivery.jobsiteId,
    materialClassification.data,
    soilClassification.data,
    edgeEstimation.data
  );

  const feedbackSnackbarManager = useFeedbackSnackbar({
    contentType: 'delivery',
    jobsiteId: delivery.jobsiteId,
    contentId: delivery.id,
  });

  const [selectedAttribute, setSelectedAttribute] = useState<string | null>();
  const [edgeSizesBoundingBoxes, setEdgeSizeBoundingBoxes] = useState<BoundingBox[]>([]);

  const handleAttributeSelect = (attributeId: string) => {
    setSelectedAttribute(selectedAttribute === attributeId ? null : attributeId);
  };

  useEffect(() => {
    if (!edgeEstimation.data || edgeEstimation.data.length === 0) return;

    const edgeData = edgeEstimation.data[0];
    if (selectedAttribute === 'edge-estimation') {
      setEdgeSizeBoundingBoxes(edgeData.boundingBoxes);
    } else if (selectedAttribute === undefined && edgeData.status === 'CRITICAL') {
      setSelectedAttribute('edge-estimation');
    } else {
      setEdgeSizeBoundingBoxes([]);
    }
  }, [selectedAttribute, edgeEstimation.data]);

  return (
    <Box
      height='calc(100% - 7rem)'
      width='100%'
      display='flex'
      boxSizing='border-box'
      flexDirection='column'
      padding={2}
      gap={1}
      overflow='auto'
    >
      <DetailImageQueryGuarded
        title={'loadingArea.title'}
        imageQuery={image}
        downloadFileName={`${delivery.delivery_id}_${pictureIdx}.jpg`}
        boundingBoxes={edgeSizesBoundingBoxes}
      />

      {/* prediction card */}
      <WrapperCardWithTitle
        title='prediction.title'
        adornment={<PredictionColoringTooltip />}
        sx={{ flex: '1 1 0', minHeight: '400px', maxHeight: '50vh' }}
      >
        <PredictionColumnsLayout
          materialTypeColumn={<MaterialColumnQueryGuarded materialClassificationQuery={materialClassification} />}
          attributesColumn={
            <AttributesColumnQueryGuarded
              attributeQueries={[soilClassification, volumeEstimation, edgeEstimation]}
              isMaterialClassificationPending={materialClassification.isPending}
              onAttributeSelect={handleAttributeSelect}
              selectedAttribute={selectedAttribute}
            />
          }
          billableMaterialColumn={<BillableMaterialColumn isUserAdmin={isUserAdmin} materials={resolvedMaterials} />}
        />
      </WrapperCardWithTitle>

      {/* 'detail-card' */}
      <WrapperCardWithTitle title='delivery.details' sx={{ flex: '1 1 0', minHeight: '400px', maxHeight: '50vh' }}>
        <DeliveryNote delivery={delivery} capturer_id={capturer_id} />
      </WrapperCardWithTitle>

      <FeedbackSnackbar {...feedbackSnackbarManager} />
    </Box>
  );
};
